<script setup>
import { onMounted, ref, watch } from 'vue';
import { useQuasar } from 'quasar';
import { UserService } from '@/services';
import { useEventStore } from '@/stores/event/event.store';
import { InputSelect } from '@/components/inputs';
import Tr from '@/i18n/translation';

const props = defineProps({
  clientId: {
    type: String,
    required: true,
  },
});

const quasar = useQuasar();
const eventStore = useEventStore();

const langList = Tr.supportedLocales;
const lang = ref(langList[0]);

const additionalSitesNum = ref(eventStore.getAdditionalSites().common.length);

watch(lang, async () => {
  eventStore.resetLangData(lang.value);
  await eventStore.tryGetLangData(lang.value);

  uploadRecords();
});

watch(
  () => eventStore.clientData.lang[lang.value],
  async () => {
    updateAdditionalSitesNumber();
    updatePlacesNumber();
  },
);

function addMissingRecords(getter, record) {
  const commonNumber = getter(lang.value).common.length;
  const langNumber = getter(lang.value).lang.length;

  for (let i = langNumber; i < commonNumber; ++i) {
    getter(lang.value).lang.push(structuredClone(record));
  }
}

function removeUnnecessaryRecords(getter) {
  const commonNumber = getter(lang.value).common.length;
  const langNumber = getter(lang.value).lang.length;

  if (commonNumber < langNumber) {
    const diff = langNumber - commonNumber;
    for (let i = 0; i < diff; ++i) {
      getter(lang.value).lang.pop();
    }
  }
}

function uploadRecords() {
  addMissingRecords(eventStore.getAdditionalSites, {
    header: '',
    buttonText: '',
    subheading: '',
  });
  addMissingRecords(eventStore.getPlaces, {
    name: '',
  });
  removeUnnecessaryRecords(eventStore.getAdditionalSites);
  removeUnnecessaryRecords(eventStore.getPlaces);

  updateAdditionalSitesNumber();
  updatePlacesNumber();
}

function updateAdditionalSitesNumber() {
  additionalSitesNum.value = eventStore.getAdditionalSites(
    lang.value,
  ).lang.length;
}

const placesNum = ref(eventStore.getPlaces(lang.value).common.length);

function updatePlacesNumber() {
  placesNum.value = eventStore.getPlaces(lang.value).lang.length;
}

async function sendEventData() {
  if (props.clientId) {
    const res = await UserService.sendEventLangData(
      props.clientId,
      eventStore.clientData.lang[lang.value],
      lang.value,
    );
    if (res) {
      quasar.notify({
        type: 'info',
        message: `Wysłano dane klienta`,
      });
    } else {
      quasar.notify({
        type: 'negative',
        message: `Błąd w przesyle danych`,
      });
    }
  } else {
    quasar.notify({
      type: 'negative',
      message: `Nie wybrano wszystkich pól`,
    });
  }
}

onMounted(() => {
  uploadRecords();
});
</script>

<template>
  <q-form ref="ClientForm" class="form" @submit.prevent="sendEventData">
    <div class="send">
      <button class="core-default-form-button" type="submit">Wyślij</button>
    </div>
    <InputSelect
      v-model="lang"
      class="input"
      :select-options="langList"
      main-color="#84491f"
      background-color="#fffbf8"
      text-font="Raleway"
      >Język</InputSelect
    >
    <div class="inputs">
      <div class="input">
        <p class="label">Nagłówek</p>
        <q-input
          v-model="eventStore.getLangAllLangData(lang).header"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Napis nad obrazkiem</p>
        <q-input
          v-model="eventStore.getLangAllLangData(lang).pictureText"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Napis pod obrazkiem</p>
        <q-input
          v-model="eventStore.getLangAllLangData(lang).underPictureText"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div class="input">
        <p class="label">Pełna data</p>
        <q-input
          v-model="eventStore.getLangAllLangData(lang).underTextData"
          class="core-default-input-form"
          borderless
        />
      </div>
      <div>
        <div v-if="additionalSitesNum !== 0" class="row-area-container"></div>
        <div
          v-for="i in 10"
          v-show="i <= additionalSitesNum"
          :key="i"
          class="row-area-container"
        >
          <div v-if="i <= additionalSitesNum" class="row-area">
            <div class="input">
              <p class="label">Nagłówek *</p>
              <q-input
                v-model="
                  eventStore.getAdditionalSitesById(i - 1, lang).lang.header
                "
                class="core-default-input-form"
                borderless
                autogrow
                lazy-rules
                :rules="[(val) => !!val || 'To pole nie może być puste']"
              />
            </div>
            <div class="input">
              <p class="label">Tekst przycisku *</p>
              <q-input
                v-model="
                  eventStore.getAdditionalSitesById(i - 1, lang).lang.buttonText
                "
                class="core-default-input-form"
                borderless
                autogrow
                lazy-rules
                :rules="[(val) => !!val || 'To pole nie może być puste']"
              />
            </div>
            <div class="input">
              <p class="label">Podpis *</p>
              <q-input
                v-model="
                  eventStore.getAdditionalSitesById(i - 1, lang).lang.subheading
                "
                class="core-default-input-form"
                borderless
                autogrow
                lazy-rules
                :rules="[(val) => !!val || 'To pole nie może być puste']"
              />
            </div>
          </div>
        </div>
      </div>
      <div>
        <div v-if="placesNum !== 0" class="row-area-container"></div>
        <div
          v-for="i in 3"
          v-show="i <= placesNum"
          :key="i"
          class="row-area-container"
        >
          <div v-if="i <= placesNum" class="row-area">
            <div class="input">
              <p class="label">Nazwa miejsca</p>
              <q-input
                v-model="eventStore.getPlacesById(i - 1, lang).lang.name"
                class="core-default-input-form"
                borderless
                autogrow
                lazy-rules
                :rules="[(val) => !!val || 'To pole nie może być puste']"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </q-form>
</template>

<style lang="scss" scoped>
@import '@/components/admin/form';
</style>
